/* oxlint-disable @phoebe/dark-mode-colors -- local harness that frames the extension like a light host page. */
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';

import { PHOEBE_EXTENSION_FRAME_NAME } from '@apps/web/lib/extension_context';
import { EXTENSION_BOTTOM_BAR_ENABLED } from '@libraries/typescript/utilities/extension_surface_flags';

export const Route = createFileRoute('/_app/(extension)/extension-local')({
    component: ExtensionLocalPage,
    head: () => ({
        meta: [{ title: 'Extension Local | Phoebe' }],
    }),
});

const SIDEPANEL_WIDTH_PX = 420;
const BOTTOM_BAR_HEIGHT_PX = 32;
const EXTENSION_IFRAME_PATH = '/extension';

type ExtensionPreviewMessage =
    | {
          readonly type: 'EXTENSION_AUTH_REFRESH_REQUEST';
      }
    | {
          readonly type: 'EXTENSION_BOTTOM_BAR_TOGGLE';
          readonly enabled: boolean;
      }
    | {
          readonly type: 'EXTENSION_OUTREACH_COUNT';
          readonly count: number;
          readonly actionableCount?: number;
      }
    | {
          readonly type: 'EXTENSION_PAGE_READY';
      };

type ExtensionPreviewPageInfo = {
    readonly provider: null;
    readonly url: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isExtensionPreviewMessage(
    value: unknown,
): value is ExtensionPreviewMessage {
    if (!isRecord(value)) {
        return false;
    }

    switch (value['type']) {
        case 'EXTENSION_AUTH_REFRESH_REQUEST':
        case 'EXTENSION_PAGE_READY':
            return true;
        case 'EXTENSION_BOTTOM_BAR_TOGGLE':
            return typeof value['enabled'] === 'boolean';
        case 'EXTENSION_OUTREACH_COUNT':
            return typeof value['count'] === 'number';
        default:
            return false;
    }
}

function buildPreviewPageInfo(): ExtensionPreviewPageInfo {
    return {
        provider: null,
        url: window.location.href,
    };
}

function buildOutreachLabel(
    outreachCount: number,
    actionableCount: number,
): string {
    const activeLabel = `${outreachCount} active outreach${outreachCount === 1 ? '' : 'es'}`;
    if (actionableCount === 0) {
        return activeLabel;
    }

    return `${activeLabel} · ${actionableCount} ${actionableCount === 1 ? 'needs' : 'need'} action`;
}

function ExtensionLocalPage() {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [iframeKey, setIframeKey] = useState(0);
    const [showBottomBar, setShowBottomBar] = useState(
        EXTENSION_BOTTOM_BAR_ENABLED,
    );
    const [outreachCount, setOutreachCount] = useState(0);
    const [actionableOutreachCount, setActionableOutreachCount] = useState(0);

    const postPreviewPageInfo = useCallback(() => {
        iframeRef.current?.contentWindow?.postMessage(
            {
                type: 'PAGE_INFO_UPDATE',
                payload: buildPreviewPageInfo(),
            },
            window.location.origin,
        );
    }, []);

    useEffect(() => {
        function handleMessage(event: MessageEvent) {
            if (event.origin !== window.location.origin) {
                return;
            }

            if (event.source !== iframeRef.current?.contentWindow) {
                return;
            }

            if (!isExtensionPreviewMessage(event.data)) {
                return;
            }

            switch (event.data.type) {
                case 'EXTENSION_AUTH_REFRESH_REQUEST':
                    setShowBottomBar(EXTENSION_BOTTOM_BAR_ENABLED);
                    setOutreachCount(0);
                    setActionableOutreachCount(0);
                    setIframeKey((currentKey) => currentKey + 1);
                    return;
                case 'EXTENSION_BOTTOM_BAR_TOGGLE':
                    setShowBottomBar(
                        EXTENSION_BOTTOM_BAR_ENABLED && event.data.enabled,
                    );
                    return;
                case 'EXTENSION_OUTREACH_COUNT':
                    setOutreachCount(event.data.count);
                    setActionableOutreachCount(event.data.actionableCount ?? 0);
                    return;
                case 'EXTENSION_PAGE_READY':
                    postPreviewPageInfo();
                    return;
                default:
                    return;
            }
        }

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [postPreviewPageInfo]);

    return (
        <div className="bg-muted/60 h-screen overflow-hidden">
            <div
                className="bg-background mx-auto flex h-full flex-col overflow-hidden shadow-[0_0_0_1px_rgba(15,23,42,0.08)]"
                style={{ width: `${SIDEPANEL_WIDTH_PX}px` }}
            >
                <iframe
                    key={iframeKey}
                    ref={iframeRef}
                    allow="storage-access"
                    className="block min-h-0 w-full flex-1 border-0 bg-white"
                    name={PHOEBE_EXTENSION_FRAME_NAME}
                    onLoad={postPreviewPageInfo}
                    src={EXTENSION_IFRAME_PATH}
                    title="Phoebe extension local preview"
                />
                {EXTENSION_BOTTOM_BAR_ENABLED && showBottomBar ? (
                    <div
                        className="flex items-center border-t border-black/10 bg-white/95 px-3 text-[11px] text-neutral-700 backdrop-blur-sm"
                        style={{ height: `${BOTTOM_BAR_HEIGHT_PX}px` }}
                    >
                        <div
                            className="inline-flex items-center overflow-hidden rounded-full"
                            style={{
                                backgroundColor:
                                    actionableOutreachCount > 0
                                        ? '#fff1e6'
                                        : '#f0f0f0',
                                color:
                                    actionableOutreachCount > 0
                                        ? '#9a3412'
                                        : '#404040',
                                boxShadow:
                                    actionableOutreachCount > 0
                                        ? 'inset 0 0 0 1px rgba(194, 65, 12, 0.14)'
                                        : 'none',
                            }}
                        >
                            <span className="px-2 py-0.5 font-medium">
                                {buildOutreachLabel(
                                    outreachCount,
                                    actionableOutreachCount,
                                )}
                            </span>
                            <span className="h-4 w-px bg-black/10" />
                            <div className="inline-flex items-center gap-1 px-2 py-0.5">
                                <span
                                    className="h-1.5 w-1.5 rounded-full"
                                    style={{ backgroundColor: '#16a34a' }}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
